/**
 * A member's group layouts, kept in step with the group while the page is open
 * (`docs/plans/group-layout-sync.md`).
 *
 * The rules are `lib/group-layouts.ts`, pure and tested there. This is the half
 * that fetches and dispatches, lifted out of `App` because `App` cannot be
 * rendered in a test (SciChart) — and every bug this plan fixes was a timing
 * bug: a listing read once at load, a replace that fired under the member's
 * cursor, a copy applied without its link. Those only show in a running hook.
 *
 * **Refetched when the Layout panel opens and when the tab comes back**, never
 * on a timer. The owner's report was "the old version until they refresh";
 * those two moments are when a member is about to look, and a poll would cost
 * a request a minute per open tab for a list that changes a few times a month.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Layout, UserWorkspace } from "@qufolio/model";

import { adoptLayout } from "../lib/layout-adopt";
import {
  adoptedNames,
  adoptsByItself,
  authorCopiesDue,
  originalsToLink,
  deferActive,
  groupDisplayName,
  layoutsToAdopt,
  legacyCopies,
  linkedCopyIndex,
  membersCanOpen,
  sectionsMembersCannotOpen,
  withMemberScreen,
  overrideTargets,
  promoteTargetKey,
  publishedKeyFor,
  withScreenOverrides,
  type Adopted,
  type LegacyAnswer,
  type LegacyCopy,
  type ScreenDefinition,
  type VirtualGroups,
} from "../lib/group-layouts";
import type { WorkspaceAction } from "../lib/workspace-actions";
import { fetchLayout, fetchLayouts, publishLayout, type LayoutSummary } from "./layouts";
import { fetchPreset } from "./presets";
import { orderTabs, screensSnapshot } from "../lib/screens";
import type { SaveStatus } from "./persistence";
import type { GroupAccount } from "./me";

/** A Shared with me row whose member copy is behind, waiting on their answer. */
export interface SharedUpdate {
  preset: LayoutSummary;
  /** The member's copy: its name, and the version and day it was taken. */
  mine: { id: string; name: string; version: number };
}

/** The three answers to a new version (`SharedUpdateDialog`). */
export type SharedUpdateAnswer = "replace" | "add" | "later";

export interface GroupLayoutSyncInput {
  workspace: UserWorkspace;
  dispatch: (action: WorkspaceAction) => void;
  status: SaveStatus;
  /** The groups this account belongs to; `null` until `/auth/me` answers. */
  memberOf: readonly string[] | null;
  /**
   * The virtual groups this account is in, with their includes — so the
   * account authoring one is not handed the included groups' layouts.
   */
  virtualGroups?: VirtualGroups;
  /** The group being authored as, if any — never adopted into, never replaced. */
  publishTarget: string | null;
  /** Index of the layout on screen — the one an in-session replace waits for. */
  activeIndex: number;
  /** Move the selection; the index is the caller's state. */
  select: (index: number) => void;
  /** Group display names learned elsewhere (the screen catalog), by key. */
  groupNames?: ReadonlyMap<string, string>;
}

/** The question on screen, with the name it is asked in. */
export interface LegacyPrompt extends LegacyCopy {
  groupName: string;
}

export interface GroupLayoutSync {
  layoutPresets: readonly LayoutSummary[];
  /** Re-read the listing. The panel's open, a returning tab, and every write call it. */
  refresh: () => Promise<void>;
  /** *Shared with me* → a layout: linked when it is a group's (step 3). */
  applyPreset: (key: string, name: string) => Promise<void>;
  /** Publish to `publishTarget`, over the key the layout already holds (step 5). */
  publish: (input: { name: string; layout: Layout }) => Promise<number | undefined>;
  /** The one-time question to show now, or `null`. */
  prompt: LegacyPrompt | null;
  answer: (choice: LegacyAnswer) => Promise<void>;
  /** A new version the member clicked in Shared with me, asked about now. */
  update: SharedUpdate | null;
  answerUpdate: (choice: SharedUpdateAnswer) => Promise<void>;
  /**
   * **Promote & Publish**: a layout from your own account straight to a
   * group's members, as that group's account for this one request. Resolves
   * to the version published.
   */
  promote: (input: { account: GroupAccount; layout: Layout; name: string }) => Promise<number | undefined>;
}

export function useGroupLayoutSync({
  workspace,
  dispatch,
  status,
  memberOf,
  virtualGroups,
  publishTarget,
  activeIndex,
  select,
  groupNames,
}: GroupLayoutSyncInput): GroupLayoutSync {
  const [layoutPresets, setLayoutPresets] = useState<readonly LayoutSummary[]>([]);
  /** A new version clicked in Shared with me, waiting on the member's answer. */
  const [update, setUpdate] = useState<SharedUpdate | null>(null);
  const updateRef = useRef(update);
  updateRef.current = update;
  /**
   * Whether the listing on hand came from a refetch rather than the page load.
   *
   * Decision 3 is about **in-session** replaces. The first listing replaces
   * every behind copy, the layout on screen included, as it always has: at load
   * the member has not started working in it, and holding it back would leave
   * the desk most members open first stale until they happened to switch away.
   */
  const [inSession, setInSession] = useState(false);

  // Read from callbacks that must not be rebuilt on every edit.
  const wsRef = useRef(workspace);
  wsRef.current = workspace;
  const presetsRef = useRef(layoutPresets);
  presetsRef.current = layoutPresets;
  const activeRef = useRef(activeIndex);
  activeRef.current = activeIndex;

  useEffect(() => {
    let alive = true;
    void fetchLayouts().then((found) => {
      if (alive) setLayoutPresets(found);
    });
    return () => {
      alive = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const found = await fetchLayouts();
    setLayoutPresets(found);
    setInSession(true);
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  const ready = (status === "idle" || status === "saving") && memberOf !== null;

  /**
   * The name each received layout arrives under — `Market Breadth (JP)` when
   * two groups publish one name (`adoptedNames`). Read by every path that makes
   * or replaces a copy, so a replace never renames the tab it swaps.
   */
  const names = useMemo(
    () => adoptedNames(layoutPresets, memberOf ?? [], publishTarget, virtualGroups, groupNames),
    [layoutPresets, memberOf, publishTarget, virtualGroups, groupNames],
  );
  const namesRef = useRef(names);
  namesRef.current = names;

  /** The one-time questions due — computed, so an answer that records the key clears its own. */
  const legacy = useMemo(
    () =>
      ready
        ? legacyCopies(layoutPresets, workspace, memberOf ?? [], publishTarget, virtualGroups, names)
            // Asked only about what is still copied in by itself: everything
            // else arrives when the member takes it, which answers it.
            .filter((copy) => adoptsByItself(copy.preset))
        : [],
    [ready, layoutPresets, workspace, memberOf, publishTarget, virtualGroups, names],
  );
  /**
   * What the adopt effect depends on, as strings — the keys being asked about
   * and every copy's link. The workspace itself changes on every pane drag, and
   * a group layout that cannot be read would then be refetched once per drag.
   */
  const askingKeys = legacy.map((copy) => copy.preset.key).join("|");
  const links = workspace.layouts
    .map((l) => (l.source ? `${l.id}:${l.source.preset}@${l.source.version ?? 0}` : ""))
    .join("|");

  /**
   * Adopt the new and replace the behind (`docs/plans/group-accounts.md` §2, §5).
   *
   * **Only once the saved workspace has loaded** — `idle` or `saving`. Before
   * that the demo workspace is on screen and the load replaces it; after a
   * failed load nothing may be written at all. A fetch that fails leaves its
   * key unrecorded, so the next load tries again. The ref keeps two renders
   * from fetching the same desks while the first batch is in flight.
   *
   * A key waiting on the one-time question is **not adopted**: copying it in
   * hidden, linked, would answer the question before it was asked and leave the
   * member a third *JAS Layout*.
   */
  const activeLayoutId = workspace.layouts[activeIndex]?.id;
  const adopting = useRef(false);
  useEffect(() => {
    if (!ready || memberOf === null) return;
    const workspace = wsRef.current;
    const asking = new Set(askingKeys.split("|"));
    // **Members choose** (owner, 2026-10-18): only a layout holding the Daily
    // Brief is copied in by itself; every other one, and every new version,
    // waits in Shared with me as ● new.
    const due = layoutsToAdopt(
      layoutPresets,
      workspace.adoptedPresets,
      memberOf,
      publishTarget,
      virtualGroups,
    ).filter((preset) => adoptsByItself(preset) && !asking.has(preset.key));
    // The account authoring a group first links its own layouts to what they
    // published, by name, at the current version — then picks up what was
    // promoted to it, and what a publish elsewhere moved on.
    const toLink = originalsToLink(layoutPresets, workspace, publishTarget);
    if (toLink.length > 0) {
      // Synchronous, and it changes `links`, so this effect runs again with
      // the links in place before anything is fetched or replaced.
      dispatch({ type: "linkSources", links: toLink });
      return;
    }
    const authored = authorCopiesDue(layoutPresets, workspace, publishTarget);
    const stale = inSession
      ? deferActive(authored.replace, workspace, activeLayoutId)
      : authored.replace;
    if ((due.length === 0 && stale.length === 0 && authored.add.length === 0) || adopting.current) {
      return;
    }
    adopting.current = true;
    const fetchAll = (presets: readonly LayoutSummary[]) =>
      Promise.all(presets.map((preset) => fetchCopy(preset, namesRef.current))).then((found) =>
        found.filter((copy): copy is Adopted => copy !== null),
      );
    void Promise.all([fetchAll(due), fetchAll(stale), fetchAll(authored.add)])
      .then(([added, replaced, linked]) => {
        if (added.length > 0) dispatch({ type: "adoptLayouts", copies: added });
        if (replaced.length > 0) dispatch({ type: "replaceLayouts", copies: replaced });
        // Shown, as a layout applied by hand is: the author is about to work on it.
        for (const copy of linked) dispatch({ type: "linkLayout", copy });
      })
      .finally(() => {
        adopting.current = false;
      });
    // The workspace is read through `adoptedPresets` and `links`; the active id
    // is a dependency so a deferred replace runs the moment the member switches.
  }, [
    ready,
    memberOf,
    virtualGroups,
    layoutPresets,
    workspace.adoptedPresets,
    links,
    publishTarget,
    inSession,
    activeLayoutId,
    askingKeys,
    dispatch,
  ]);

  const applyPreset = useCallback(
    async (key: string, name: string) => {
      const preset = presetsRef.current.find((p) => p.key === key);
      const isGroup = preset?.ownerKind === "org";
      // **One linked copy per group layout.** Applying it again shows the one
      // the member holds rather than adding a second — the duplicate the owner
      // saw is exactly two copies of one key.
      if (isGroup) {
        const at = linkedCopyIndex(wsRef.current, key);
        const held = at >= 0 ? wsRef.current.layouts[at]! : undefined;
        const had = held?.source?.version ?? 0;
        if (held && preset?.version !== undefined && had < preset.version) {
          // A new version: the member's to take (Replace / Add as / Not now).
          setUpdate({ preset, mine: { id: held.id, name: held.name, version: had } });
          return;
        }
        if (at >= 0) {
          if (wsRef.current.layouts[at]!.hidden === true) {
            dispatch({ type: "setLayoutHidden", layoutId: wsRef.current.layouts[at]!.id, hidden: false });
          }
          select(at);
          return;
        }
      }
      // A 404 here means the grant went away between the listing and the click,
      // which is rare and recoverable by reopening the panel — so it is a
      // no-op rather than an error nobody can act on.
      if (isGroup) {
        // Through `fetchCopy`, so a copy applied by hand is named and
        // overridden exactly as an adopted one is — it is the same copy.
        const copy = await fetchCopy(preset, namesRef.current);
        if (!copy) return;
        const at = wsRef.current.layouts.length;
        dispatch({ type: "linkLayout", copy });
        select(at);
        return;
      }
      const definition = await fetchLayout(key);
      if (!definition) return;
      const at = wsRef.current.layouts.length;
      dispatch({ type: "addLayout", layout: adoptLayout(definition, name) });
      select(at);
    },
    [dispatch, select],
  );

  const publish = useCallback(
    async (input: { name: string; layout: Layout }) => {
      if (!publishTarget) return undefined;
      const key = publishedKeyFor(input.layout, input.name, presetsRef.current, publishTarget);
      const layout = await forMembers(input.layout, publishTarget);
      const published = await publishLayout({
        name: input.name,
        description: "",
        ownerKey: publishTarget,
        layout,
        ...(key ? { key } : {}),
      });
      // The layout published is now that version: linked, so a publish from
      // elsewhere later reads as newer and replaces it here.
      if (published.version !== undefined) {
        dispatch({
          type: "linkSources",
          links: [{ layoutId: input.layout.id, key: published.key, version: published.version }],
        });
      }
      await refresh();
      // What `PublishedMenu` compares with the version it showed: equal means
      // the server found nothing changed.
      return published.version;
    },
    [publishTarget, refresh, dispatch],
  );

  /**
   * Keys being answered, or whose fetch failed this session — hidden from the
   * question so it is not asked twice while the definition is in flight, and
   * not re-asked in a loop when the group layout cannot be read. The next load
   * asks again: nothing was recorded.
   */
  const [settling, setSettling] = useState<ReadonlySet<string>>(new Set());
  const next = legacy.find((copy) => !settling.has(copy.preset.key));
  const prompt: LegacyPrompt | null = next
    ? {
        ...next,
        groupName: groupDisplayName(next.preset.ownerKey!, next.preset.ownerName, groupNames),
      }
    : null;
  const promptRef = useRef(prompt);
  promptRef.current = prompt;

  const answer = useCallback(
    async (choice: LegacyAnswer) => {
      const asked = promptRef.current;
      if (!asked) return;
      const key = asked.preset.key;
      setSettling((had) => new Set(had).add(key));
      const copy = await fetchCopy(asked.preset, namesRef.current);
      if (!copy) return;
      const ws = wsRef.current;
      // The selection follows its layout's id: removing the redundant copies
      // shifts every later index, and the answer must not retarget the desk on
      // screen (`lib/layout-visibility.ts`'s `selectionAfterRemove` rule).
      const activeId = ws.layouts[activeRef.current]?.id;
      const drop = new Set(asked.redundantIds);
      dispatch({
        type: "resolveLegacyLayout",
        legacy: { key, mineId: asked.mineId, redundantIds: asked.redundantIds },
        copy,
        answer: choice,
        groupName: asked.groupName,
      });
      if (drop.size > 0 && activeId !== undefined) {
        const moved = ws.layouts.filter((l) => !drop.has(l.id)).findIndex((l) => l.id === activeId);
        if (moved >= 0) select(moved);
      }
      setSettling((had) => {
        const left = new Set(had);
        left.delete(key);
        return left;
      });
    },
    [dispatch, select],
  );

  const answerUpdate = useCallback(
    async (choice: SharedUpdateAnswer) => {
      const asked = updateRef.current;
      setUpdate(null);
      if (!asked || choice === "later") return;
      const copy = await fetchCopy(asked.preset, namesRef.current);
      if (!copy) return;
      if (choice === "replace") {
        // Swapped in place — same position, a fresh id — so the position is
        // what the selection follows.
        const at = wsRef.current.layouts.findIndex((l) => l.id === asked.mine.id);
        dispatch({ type: "replaceLayouts", copies: [copy] });
        if (at >= 0) select(at);
        return;
      }
      const at = wsRef.current.layouts.length;
      const name = `${copy.layout.name} v${copy.version ?? asked.preset.version ?? ""}`.trim();
      dispatch({ type: "addLayoutVersion", copy: { ...copy, layout: { ...copy.layout, name } } });
      select(at);
    },
    [dispatch, select],
  );

  const promote = useCallback(
    async ({ account, layout, name }: { account: GroupAccount; layout: Layout; name: string }) => {
      const published = await publishLayout({
        name: name.trim(),
        description: "",
        ownerKey: account.group,
        layout: await forMembers(layout, account.group),
        key: promoteTargetKey(presetsRef.current, account.group, layout),
        actAs: account.key,
      });
      await refresh();
      return published.version;
    },
    [refresh],
  );

  return { layoutPresets, refresh, applyPreset, publish, prompt, answer, update, answerUpdate, promote };
}

/**
 * One preset's definition, made this workspace's; `null` when it cannot be read.
 *
 * Named as `adoptedNames` says, and with the listing row's screener overrides
 * applied (`docs/plans/group-sharing.md`) — every path that makes or replaces a
 * member's copy comes through here, so the override cannot be applied on adopt
 * and forgotten on replace. The `to` screens are fetched only when a screener
 * in the layout shows a `from`.
 */
async function fetchCopy(
  preset: LayoutSummary,
  names?: ReadonlyMap<string, string>,
): Promise<Adopted | null> {
  const definition = await fetchLayout(preset.key);
  if (!definition) return null;
  let layout = adoptLayout(definition, names?.get(preset.key) ?? preset.name);
  const overrides = preset.screenOverrides ?? [];
  const wanted = overrides.length > 0 ? overrideTargets(layout, overrides) : [];
  if (wanted.length > 0) {
    const found = await Promise.all(
      wanted.map(async (key) => [key, await fetchPreset(key)] as const),
    );
    const definitions = new Map<string, ScreenDefinition>();
    for (const [key, screen] of found) if (screen) definitions.set(key, screen);
    layout = withScreenOverrides(layout, overrides, definitions);
  }
  return {
    key: preset.key,
    layout,
    ...(preset.version !== undefined ? { version: preset.version } : {}),
  };
}

/**
 * The layout as members get it: a screener showing a screen they cannot open —
 * the author's own (a Recs preview) or another group's — shows the group's
 * first screen instead, with that screen's whole definition (owner,
 * 2026-10-16). The author's own layout is untouched; only the published copy.
 * With no screen of the group to fall back to, the layout goes as it is.
 */
async function forMembers(layout: Layout, group: string): Promise<Layout> {
  const screens = screensSnapshot();
  const blocked = sectionsMembersCannotOpen(layout, screens, group);
  if (blocked.length === 0) return layout;
  const instead = orderTabs(screens).find((s) => membersCanOpen(s.key, screens, group));
  if (!instead) return layout;
  const definition = await fetchPreset(instead.key);
  return withMemberScreen(layout, blocked, { key: instead.key, ...(definition ?? {}) });
}
